<template>
    <div>
        <pc-alert
            v-model:shown="showAlert"
            v-bind:severity="alertWarning"
            class="mb-3">
            {{ captionText }}
        </pc-alert>
        <pc-radio-group
            id="showStorageEquipmentRadioGroup"
            v-model="showStorageEquipmentOptionValue"
            required
            v-bind:label="showStorageEquipmentOptionLabelText"
            v-bind:options="showStorageEquipmentOptions"
            v-bind:readonly="readonly" />
        <div
            v-if="hasBooleanFormulaOptions"
            class="indent-formula-select">
            <pc-select
                id="selectedFormulaId"
                ref="formulaSelect"
                v-model="selectedFormulaId"
                v-bind:alt-label="selectedFormulaIdAltLabel"
                v-bind:disabled="formulaSelectIsDisabled"
                v-bind:options="booleanFormulaOptions"
                v-bind:readonly="readonly"
                v-bind:validator="selectedFormulaValidator"
                v-on:validated="onValidated">
                <option value="">
                    {{ defaultFormulaOption }}
                </option>
            </pc-select>
        </div>
        <div v-if="selectedFormulaObj">
            <hr />
            <pc-loader v-bind:loading="!selectedFormulaObj.RootExpression">
                {{ formulaDiagramHeaderLabelText }}
                <formula
                    v-if="selectedFormulaObj.RootExpression"
                    v-bind:formula="selectedFormulaObj"
                    simple />
            </pc-loader>
        </div>
    </div>
</template>

<script setup>
    import { ref, inject, computed, watch, useTemplateRef } from "vue";
    import { useStore } from "vuex";
    import { PAGE_APP_SYMBOL } from "_Main/injectionSymbols";
    import { PROGRAM_TYPES } from "SharedStores/Program/programStore";
    import formula from "Formula/Components/formula.vue";
    import { DIALOG_RESULT } from "Components/pc-dialog.vue";
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import { PV_SHOW_STORAGE_EQUIPMENT_OPTION_VALUE } from "Forms/formConstants";
    import { ALERT_SEVERITY } from "Util/alerts";
    import { useResource } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/pvShowStorageEquipmentDialog.i18n";

    const app = inject(PAGE_APP_SYMBOL);
    const store = useStore();

    const props = defineProps({
        readonly: { type: Boolean, required: true },
        captionText: { type: String, required: true },
        dataFieldsEditorObject: { type: Object, required: true },
        includeNeverOption: { type: Boolean, required: true },
        showStorageEquipment: { type: Boolean, required: true },
        showStorageEquipmentFormulaId: { type: String, required: true },
        booleanFormulaOptions: { type: Array, required: true }
    });

    const showAlert = ref(true);
    const alertWarning = ALERT_SEVERITY.warning;

    const showStorageEquipmentOptionValue = ref(
        props.showStorageEquipment
            ? props.showStorageEquipmentFormulaId
                ? PV_SHOW_STORAGE_EQUIPMENT_OPTION_VALUE.formula
                : PV_SHOW_STORAGE_EQUIPMENT_OPTION_VALUE.always
            : PV_SHOW_STORAGE_EQUIPMENT_OPTION_VALUE.never
    );

    const formulaSelect = useTemplateRef("formulaSelect");

    const selectedFormulaId = ref(props.showStorageEquipmentFormulaId);

    const selectedFormulaValidator = getValidator(app, [
        {
            type: VALIDATOR_TYPES.custom,
            validate: selectedFormulaIdIsOk,
            errorMsg: app.res({
                namespace: NAMESPACE,
                key: KEYS.formulaRequiredValidationErrorMessage
            })
        }
    ]);

    const validationResult = ref(null);

    const showStorageEquipmentOptions = computed(() => {
        const options = [];
        if (props.includeNeverOption) {
            options.push({
                value: PV_SHOW_STORAGE_EQUIPMENT_OPTION_VALUE.never,
                text: showStorageEquipmentNeverOptionText
            });
        }
        options.push({
            value: PV_SHOW_STORAGE_EQUIPMENT_OPTION_VALUE.always,
            text: showStorageEquipmentAlwaysOptionText
        });
        if (hasBooleanFormulaOptions.value) {
            options.push({
                value: PV_SHOW_STORAGE_EQUIPMENT_OPTION_VALUE.formula,
                text: showStorageEquipmentFormulaOptionText
            });
        }
        return options;
    });

    const hasBooleanFormulaOptions = computed(
        () => props.booleanFormulaOptions && props.booleanFormulaOptions.length > 0
    );

    const formulaSelectIsDisabled = computed(() => {
        if (PV_SHOW_STORAGE_EQUIPMENT_OPTION_VALUE) {
            return showStorageEquipmentOptionValue.value !== PV_SHOW_STORAGE_EQUIPMENT_OPTION_VALUE.formula;
        }
        return true;
    });

    const defaultFormulaOption = computed(() => {
        if (showStorageEquipmentOptionValue.value === PV_SHOW_STORAGE_EQUIPMENT_OPTION_VALUE.formula) {
            return formulaSelectDefaultOptionText;
        }
        return "";
    });

    const hasValidationError = computed(() => {
        if (validationResult.value) {
            return validationResult.value.hasError;
        }
        return false;
    });

    const selectedFormulaObj = computed(() => {
        if (!selectedFormulaId.value) {
            return null;
        }
        const formulaObj = store.getters[PROGRAM_TYPES.getters.formulaFromId](selectedFormulaId.value);
        if (!formulaObj.RootExpression) {
            loadFormulaEvaluator(formulaObj.PublicId);
        }
        return formulaObj;
    });

    // i18n
    const {
        showStorageEquipmentOptionLabelText,
        selectedFormulaIdAltLabel,
        formulaDiagramHeaderLabelText,
        showStorageEquipmentNeverOptionText,
        showStorageEquipmentAlwaysOptionText,
        showStorageEquipmentFormulaOptionText,
        formulaSelectDefaultOptionText
    } = useResource(KEYS, NAMESPACE);

    watch(showStorageEquipmentOptionValue, () => {
        if (showStorageEquipmentOptionValue.value !== PV_SHOW_STORAGE_EQUIPMENT_OPTION_VALUE.formula) {
            selectedFormulaId.value = "";
            if (hasBooleanFormulaOptions.value) {
                formulaSelect.value.validate();
                validationResult.value = formulaSelect.value.validationResult;
            }
        }
    });

    function canClose(result) {
        if (result === DIALOG_RESULT.cancel) {
            return true;
        }
        if (hasBooleanFormulaOptions.value) {
            formulaSelect.value.validate();
            validationResult.value = formulaSelect.value.validationResult;
            return !hasValidationError.value;
        }
        return true;
    }

    function resultHook() {
        return {
            showStorageEquipmentOptionValue: showStorageEquipmentOptionValue.value,
            selectedFormulaId: selectedFormulaId.value
        };
    }

    function selectedFormulaIdIsOk() {
        // It's only required if the formula option is selected
        if (showStorageEquipmentOptionValue.value === PV_SHOW_STORAGE_EQUIPMENT_OPTION_VALUE.formula) {
            return Boolean(selectedFormulaId.value);
        } else {
            return true;
        }
    }

    function onValidated(result) {
        validationResult.value = result;
    }

    async function loadFormulaEvaluator(formulaId) {
        const result = await app.GetFormulaEvaluator({
            programId: app.ProgramId,
            formulaId: formulaId
        });
        store.commit(PROGRAM_TYPES.mutations.addField, { formula: result.Data });
    }

    defineExpose({ canClose, resultHook });
</script>

<style lang="scss" scoped>
    .indent-formula-select {
        margin-left: 2rem;
    }
</style>
